import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import BlockList from 'components/application/BlockList';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import PersonnelTrainingStatusIndicator from 'components/personnel/PersonnelTrainingStatusIndicator';

import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function RolesSidePanelPersonnelTab({
  domainPersonnel,
  isLoadMorePersonnelVisible,
  loadMoreRef,
}) {
  const { hasPersonnelViewableAccess } = useTrainingRegisterResources();

  const { t: i18nTPersonnel } = useTranslation('common', {
    keyPrefix: 'personnel',
  });

  return (
    <div key='personnelTab' label='personnel'>
      {domainPersonnel?.data.length === 0 ?
        <li className='block-list__item tw-border-grey-100'>
          <div className='block-list__item--flex-container'>
            <div className='block-list__item--align-center'>
              <span>{i18nTPersonnel('noPersonnelHaveThisRole')}</span>
            </div>
          </div>
        </li>
      : <>
          <BlockList
            blockListItemClasses={(person) =>
              person.relationships.subcontractor.data ?
                ' block-list__item--double'
              : ''
            }
            collection={domainPersonnel?.data || []}
            onItemClick={
              hasPersonnelViewableAccess ?
                (person) => window.location.replace(`personnel/${person.id}`)
              : undefined
            }
            onItemDisplay={(person) => {
              const profilePhoto =
                domainPersonnel.included.profilePhoto[
                  person.relationships.profilePhoto.data?.id
                ];

              const subcontractor =
                domainPersonnel.included.subcontractor[
                  person.relationships.subcontractor.data?.id
                ];

              return (
                <div className='block-list__item--flex-container tw-items-center'>
                  <div className='block-list__item--align-center tw-mr-3'>
                    <PersonnelTrainingStatusIndicator
                      status={person.attributes.trainingStatus}
                    />
                  </div>
                  <ProfilePhotoDisplay
                    componentSize='sm'
                    photoUrl={profilePhoto?.links.croppedUrl}
                    profileInitials={`${person.attributes.firstName[0]}${person.attributes.lastName[0]}`}
                  />
                  <div className='block-list__item--text mw-300'>
                    <span className='truncated-text-container'>
                      {personDisplayName({ attributes: person.attributes })}
                    </span>
                    {person.relationships.subcontractor.data && (
                      <span className='block-list__item--sub-text truncated-text-container tw-text-s tw-tracking-wide tw-text-grey-500'>
                        {subcontractor?.attributes.name}
                      </span>
                    )}
                  </div>
                </div>
              );
            }}
          />
          {isLoadMorePersonnelVisible && (
            <li
              className='block-list__item tw-border-grey-100'
              ref={loadMoreRef}
            >
              <div className='block-list__item--flex-container'>
                <div className='block-list__item--align-center'>
                  <span>{i18nTPersonnel('loadingPersonnel')}</span>
                </div>
              </div>
            </li>
          )}
        </>
      }
    </div>
  );
}

RolesSidePanelPersonnelTab.propTypes = {
  domainPersonnel: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('personnel')).isRequired,
    included: PropTypes.shape({
      profilePhoto: PropTypes.objectOf(resourceShape('profilePhoto'))
        .isRequired,
      subcontractor: PropTypes.objectOf(resourceShape('subcontractor'))
        .isRequired,
    }).isRequired,
  }),
  isLoadMorePersonnelVisible: PropTypes.bool.isRequired,
  loadMoreRef: PropTypes.func.isRequired,
};
